import { Alert, VFlow } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { useFlags } from 'config/useFlagsContext'
import { AtendimentoProfissional, FonteIdadeGestacionalEnum, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { AcompanhamentoVulnerabilidadeSection } from 'view/atendimentos/detail/soap/aside/acompanhamento-vulnerabilidade/AcompanhamentoVulnerabilidadeSection'
import { IdadeGestacionalAcompanhamentoPreNatal } from 'view/atendimentos/detail/soap/aside/pre-natal/model-acompanhamentoPreNatal'
import { grupoCboVisualizarDetalhesEncaminhamentosNoAcompPreNatal } from 'view/cidadao/authorization/acessos'

import { UltimosContatosCard } from '../../components/UltimosContatosCard'
import { ExternalPaths } from '../../model-acompanhamentos'
import { AutorreferidasCard } from './AutorreferidasCard'
import { EncaminhamentosAntigosGestacaoCard } from './EncaminhamentosAntigosGestacaoCard'
import { EncaminhamentosGestacaoCard } from './EncaminhamentosGestacaoCard'
import { ExamesPreNatalCard } from './ExamesPreNatalCard'
import { InformacoesGestacaoAtualCard } from './InformacoesGestacaoAtualCard'
import { ListaProblemasCondicoesCard } from './ListaProblemasCondicoesCard'
import { MedicamentosEmUsoCard } from './MedicamentosEmUsoCard'
import { MedicoesGestacaoCard } from './MedicoesGestacaoCard'
import { ProximosAgendamentosCard } from './ProximosAgendamentosCard'
import { VacinacaoGestacaoCard } from './VacinacaoGestacaoCard'

interface InformacoesGestacaoTabContentProps {
  cidadaoId: ID
  prontuarioId: ID
  atendimentoId: ID
  dataReferencia: Instant
  loading: boolean
  dum: Date
  idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal
  dataInicioGestacao: Date
  fonteIdadeGestacional: FonteIdadeGestacionalEnum
  dppEcografica: Date
  atendimentosProfUltimaGestacao: AtendimentoProfissional[]
  medicoesPath: string
  externalPaths: ExternalPaths
  isVisualizacaoProntuario: boolean
  isEscutaInicial: boolean
}

export const InformacoesGestacaoTabContent = (props: InformacoesGestacaoTabContentProps) => {
  const {
    cidadaoId,
    prontuarioId,
    atendimentoId,
    loading,
    dataReferencia,
    dum,
    idadeGestacional,
    dataInicioGestacao,
    fonteIdadeGestacional,
    dppEcografica,
    atendimentosProfUltimaGestacao,
    medicoesPath,
    externalPaths,
    isVisualizacaoProntuario,
    isEscutaInicial,
  } = props

  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()
  const { hasCboAuth } = useSession()

  return (
    <VFlow>
      {!isVisualizacaoProntuario && (
        <Alert type='info'>Os dados inseridos no atendimento atual só serão exibidos no próximo atendimento.</Alert>
      )}
      <InformacoesGestacaoAtualCard
        cidadaoId={cidadaoId}
        prontuarioId={prontuarioId}
        atendimentoId={atendimentoId}
        loading={loading}
        dum={dum}
        idadeGestacional={idadeGestacional}
        dataInicioGestacao={dataInicioGestacao}
        fonteIdadeGestacional={fonteIdadeGestacional}
        dppEcografica={dppEcografica}
        atendimentosProfUltimaGestacao={atendimentosProfUltimaGestacao}
        dataReferencia={dataReferencia}
      />
      <AcompanhamentoVulnerabilidadeSection
        cidadaoId={cidadaoId}
        dataReferencia={dataReferencia}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
      />
      <MedicoesGestacaoCard
        prontuarioId={prontuarioId}
        dataInicioGestacao={dataInicioGestacao}
        atendimentosProfUltimaGestacao={atendimentosProfUltimaGestacao}
        medicoesPath={medicoesPath}
      />
      <MedicamentosEmUsoCard prontuarioId={prontuarioId} tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL} />
      <ListaProblemasCondicoesCard
        prontuarioId={prontuarioId}
        dataInicioGestacao={dataInicioGestacao}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
      />
      <AutorreferidasCard cidadaoId={cidadaoId} />
      <UltimosContatosCard
        cidadaoId={cidadaoId}
        atendimentoId={atendimentoId}
        dataInicioCondicao={dataInicioGestacao}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
        historicoPath={externalPaths.historicoPath}
        isEscutaInicial={isEscutaInicial}
      />
      <VacinacaoGestacaoCard
        dataInicioGestacao={dataInicioGestacao}
        vacinacaoPath={externalPaths.vacinacaoPath}
        loading={loading}
        prontuarioId={prontuarioId}
        isEscutaInicial={isEscutaInicial}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
      />
      <ExamesPreNatalCard prontuarioId={prontuarioId} dataInicioGestacao={dataInicioGestacao} loading={loading} />
      {hasCboAuth(grupoCboVisualizarDetalhesEncaminhamentosNoAcompPreNatal) &&
        /* TODO @RoyalFlush (#32854) Excluir o EncaminhamentosAntigosGestacaoCard e manter apenas EncaminhamentosGestacaoCard 
        quando os encaminhamentos forem unificados com compartilhamento de cuidado sem a flag no SOAP */
        (PILOTO_VINCULACAO_CUIDADO_ENABLED ? (
          <EncaminhamentosGestacaoCard
            prontuarioId={prontuarioId}
            dataInicioCondicao={dataInicioGestacao}
            tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
          />
        ) : (
          <EncaminhamentosAntigosGestacaoCard prontuarioId={prontuarioId} dataInicioGestacao={dataInicioGestacao} />
        ))}
      <ProximosAgendamentosCard
        cidadaoId={cidadaoId}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
        agendamentosPath={externalPaths.agendamentosPath}
      />
    </VFlow>
  )
}
